import { EventFilterValue, EventListTeamInfo, MergedGpEventResponse } from "../../../types";
import { RefObject, useEffect, useRef, useState } from "react";
import { EventItem } from "./EventItem";
import { MediaPlayerInstance } from "@vidstack/react";
import { _ } from "i18n";
import { EventItemDummy } from "./EventItemDummy";

interface EventListListProps {
	filteredFilterValues: EventFilterValue[];
	mergedEvents: MergedGpEventResponse[];
	isClip: boolean;
	selectionAllowed: boolean;
	selectionAtMax?: boolean;
	teamInfo: EventListTeamInfo;
	playerRef: RefObject<MediaPlayerInstance> | null;
	onWheel: (follow: boolean) => void;
	onSelectEvent: (event: MergedGpEventResponse) => void;
	selectedEvents: MergedGpEventResponse[];
	currentTime: number;
	eventCommentsPermission: boolean;
	onOpenComments: (event: MergedGpEventResponse) => void;
}

export function EventListList({
	filteredFilterValues,
	mergedEvents,
	isClip,
	selectionAllowed,
	selectionAtMax = false,
	teamInfo,
	playerRef,
	onWheel,
	onSelectEvent,
	selectedEvents,
	currentTime,
	eventCommentsPermission,
	onOpenComments,
}: EventListListProps) {
	const containerRef = useRef<HTMLDivElement>(null);

	const observerRef = useRef<IntersectionObserver | null>(null);

	const [visibleDivs, setVisibleDivs] = useState<Set<number>>(new Set());

	const selectedEventIds = new Set(selectedEvents.map((event) => event.id));

	useEffect(() => {
		observerRef.current = new IntersectionObserver(
			(entries) => {
				setVisibleDivs((prev) => {
					const newVisible = new Set(prev);
					entries.forEach((entry) => {
						const divIndex = parseInt(entry.target.getAttribute("data-index") || "0");
						const firstChild = entry.target.children[0];
						const inEdit = firstChild?.getAttribute("data-in-edit");
						if (entry.isIntersecting || inEdit) {
							newVisible.add(divIndex);
						} else {
							newVisible.delete(divIndex);
						}
					});
					return newVisible;
				});
			},
			{
				threshold: 0.1,
			}
		);
		if (observerRef.current && containerRef.current) {
			const divs = containerRef.current.querySelectorAll(".event");
			divs.forEach((div) => {
				observerRef.current?.observe(div);
			});
		}
		return () => {
			observerRef.current?.disconnect();
		};
	}, [mergedEvents]);

	return (
		<div className="eventList-events" ref={containerRef} onWheel={(e) => (Math.abs(e.deltaY) > 20 ? onWheel(false) : undefined)}>
			{filteredFilterValues.length === 0 && mergedEvents.length === 0 && !isClip && (
				<div className="eventList-noFilterMessage">{_("label.gamePage.events.noFilter")}</div>
			)}
			{mergedEvents.map((event, index) => {
				const isVisible = visibleDivs.has(index);
				const isLastItem = index === mergedEvents.length - 1;
				const hasProgress = currentTime > event.startTime && currentTime <= event.endTime;
				// Always render full EventItem for the last item so it doesn't show as empty (last item is often just outside viewport and gets dummy otherwise)
				const showFullItem = isVisible || hasProgress || isLastItem;
				return (
					<div key={index} data-index={index} className="event" data-start-time={event.startTime} data-end-time={event.endTime}>
						{!showFullItem ? (
							<EventItemDummy event={event} />
						) : (
							<EventItem
								teamInfo={teamInfo}
								isClip={isClip}
								playerRef={playerRef}
								mergedEvents={mergedEvents}
								containerRef={containerRef}
								counter={index + 1}
								mergedEvent={event}
								key={event.id}
								onSelectEvent={onSelectEvent}
								selectionAllowed={selectionAllowed}
								selectionDisabled={selectionAtMax && selectedEventIds.has(event.id) === false}
								isSelected={selectedEventIds.has(event.id)}
								currentTime={currentTime}
								hasProgress={hasProgress}
								eventCommentsPermission={eventCommentsPermission}
								onOpenComments={onOpenComments}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
}
